import { useState } from 'react';
import styles from '../article.module.css';
import { MAX_TEXT_COMPONENT } from '../Article.context';
import { StyledText } from './Text';
import { StyledImage } from './Image';
import { StyledAdvert } from './Offer';
import { StyledInfogram } from './Infogram';
import { StyledSpacer } from './Spacer';
import { StyledButton } from './Button';
import { Anchor } from './Anchor';
import { StyledVideo } from './Video';
import { StyledAudio } from './Audio';
import { StyledTwitterTimeline } from './Twitter';
import { StyledDivider } from './Divider';
import { StyledColumns } from './Columns';
import { StyledGallery } from './Gallery';
import { StyledCustom } from './Custom';
import { DeviceType } from '../CSS';

const textComponentsSet = new Set([
	'headline',
	'title',
	'subtitle',
	'intro',
	'body',
	'crosshead',
	'byline',
	'blockquote',
	'footer',
	'imagecaption',
]);

for (let i = 0; i <= MAX_TEXT_COMPONENT; i++) {
	textComponentsSet.add(`text${i + 1}`);
}

export const StyledTabs = (props: StyledTabsProps) => {
	const { id, tabs, columns, language, device } = props;
	return (
		<Tabs
			device={device}
			id={id}
			tabs={tabs}
			columns={columns}
			language={language}
		/>
	);
};

interface StyledTabsProps {
	id: string;
	tabs: Array<any>;
	device: DeviceType;
	columns: Array<any>;
	language: string;
}

export const Tabs = (props: TabsProps) => {
	let { id, tabs, columns, language, device } = props;
	const [selected, setSelected] = useState(0);
	if (!language) {
		language = 'en';
	}
	if (!tabs.length || !columns.length || tabs.length !== columns.length) {
		return null;
	}

	return (
		<section id={id} className={[styles['tabs'], 'tabs'].join(' ')}>
			<div>
				<nav>
					{tabs.map((tab, i) => (
						<Tab
							key={i}
							index={i}
							tab={tab}
							isSelected={selected === i}
							setIndex={setSelected}
							label={tab?.label[language]}
						/>
					))}
				</nav>
				<Content device={device} components={columns[selected]} />
			</div>
		</section>
	);
};

interface TabsProps {
	id: string;
	tabs: Array<any>;
	device: DeviceType;
	columns: Array<any>;
	language: string;
}

const Tab = (props: TabProps) => {
	const { index, isSelected, setIndex, label } = props;
	const classNames = [styles['tab'], 'tab'];
	if (isSelected) {
		classNames.push(styles['selected']);
		classNames.push('selected');
	}
	return (
		<div
			className={classNames.join(' ')}
			onClick={() => {
				setIndex(index);
			}}
		>
			{label}
		</div>
	);
};

interface TabProps {
	index: number;
	isSelected?: boolean;
	setIndex?: any;
	tab?: any;
	label?: string;
}

const Content = ({ components, device }: ContentProps) => {
	return (
		<div className={[styles['content'], 'content'].join(' ')}>
			{components
				.filter(byDevice(device))
				.filter((i) => !!i)
				.map(byComponent)
				.filter((i) => !!i)}
		</div>
	);
};

interface ContentProps {
	device: DeviceType;
	components: Array<any>;
}

const byDevice = (device: DeviceType) => {
	return ({ devices }: any) => {
		if (
			(devices.phone === 'off' && device === 'mobile') ||
			(devices.tablet === 'off' && device === 'tablet') ||
			(devices.desktop === 'off' && device === 'desktop')
		) {
			return false;
		}

		return true;
	};
};

const byComponent = (component: any, i: number) => {
	if (textComponentsSet.has(component.component)) {
		return <StyledText key={i} {...component} />;
	}
	switch (component.component) {
		case 'image':
			return <StyledImage key={i} {...component} />;
		case 'advert':
			return <StyledAdvert key={i} {...component} />;
		case 'infogram':
			return <StyledInfogram key={i} {...component} />;
		case 'map':
			return null;
		// return <StyledMap key={i}  {...component}/>;
		case 'spacer':
			return <StyledSpacer key={i} {...component} />;
		case 'button':
			return <StyledButton key={i} {...component} />;
		case 'anchor':
			return <Anchor key={i} {...component} />;
		case 'video':
			return <StyledVideo key={i} {...component} />;
		case 'audio':
			return <StyledAudio key={i} {...component} />;
		case 'twitter':
			return <StyledTwitterTimeline key={i} {...component} />;
		case 'divider':
			return <StyledDivider key={i} {...component} />;
		case 'columns':
			return <StyledColumns key={i} {...component} />;
		case 'gallery':
			if (component['control-speed']) {
				component.control_speed = component['control-speed'];
			}
			return <StyledGallery key={i} {...component} />;
		case 'custom':
			return <StyledCustom key={i} {...component} />;
		default:
			return null;
	}
};
